import { useEffect, useRef } from 'react';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type { ApproverUserDto } from '@proposify/document-service-api-client';
import { useCurrentUser } from 'providers/UserProvider';
import { approvalQueries } from 'services/documents/approvalQueries';
import type { DocumentStatus } from 'services/documents/entities';
import { QueryKeys } from 'utils/queryKeys';
import { selectCurrentSaveStatus } from '../components/editor/grid/reduxStore/saveStatusSlice';
import { useAppSelector } from '../components/editor/grid/reduxStore/Store';
import { DocumentSaveStatus } from '../components/editor/shared/models/DocumentSaveStatus';

export interface ApprovalAccess {
  requiresApproval: boolean;
  canApprove: boolean;
  status: DocumentStatus;
  eligibleApprovers: ApproverUserDto[];
}

export function useApprovalAccess(contentId: string): ApprovalAccess {
  const { data: currentUser } = useCurrentUser();
  const { data } = useSuspenseQuery(approvalQueries.state(contentId, currentUser.accountId, currentUser.id));

  const queryClient = useQueryClient();
  const saveStatus = useAppSelector(selectCurrentSaveStatus).status;
  const previousSaveStatus = useRef(saveStatus);

  useEffect(() => {
    if (saveStatus === DocumentSaveStatus.SAVED && previousSaveStatus.current !== DocumentSaveStatus.SAVED) {
      queryClient.invalidateQueries({ queryKey: QueryKeys.getDocumentApprovalStateBase(contentId) });
    }
    previousSaveStatus.current = saveStatus;
  }, [saveStatus, contentId, queryClient]);

  return {
    requiresApproval: data.requiresApproval,
    status: data.status,
    eligibleApprovers: data.eligibleApprovers,
    canApprove: data.canApprove,
  };
}
